import React, { useEffect, useCallback, useState, useRef } from 'react';

const LogBookHubPage = () => {
  const [slideIndex, setSlideIndex] = useState(1);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // State for mobile menu
  const [showConfirmationOverlay, setShowConfirmationOverlay] = useState(false); // State for confirmation overlay
  const [showErrorOverlay, setShowErrorOverlay] = useState(false); // State for error overlay
  const [overlayMessage, setOverlayMessage] = useState(''); // State for overlay message content

  const slidesRef = useRef([]); // Will be populated dynamically
  const dotsRef = useRef([]); // Will be populated dynamically
  const reviewTrackRef = useRef(null);
  const reviewItemsRef = useRef([]);
  const emailInputRef = useRef(null);
  const subscriptionMessageRef = useRef(null);

  const showSlides = useCallback((n) => {
    if (slidesRef.current.length === 0 || dotsRef.current.length === 0) return;

    let newIndex = n;
    if (n > slidesRef.current.length) {
      newIndex = 1;
    }
    if (n < 1) {
      newIndex = slidesRef.current.length;
    }
    setSlideIndex(newIndex);

    for (let i = 0; i < slidesRef.current.length; i++) {
      if (slidesRef.current[i]) slidesRef.current[i].style.display = "none";
    }
    for (let i = 0; i < dotsRef.current.length; i++) {
      if (dotsRef.current[i]) dotsRef.current[i].className = dotsRef.current[i].className.replace(" active", "");
    }
    if (slidesRef.current[newIndex - 1]) slidesRef.current[newIndex - 1].style.display = "block";
    if (dotsRef.current[newIndex - 1]) dotsRef.current[newIndex - 1].className += " active";
  }, []);

  const plusSlides = useCallback((n) => {
    showSlides(slideIndex + n);
  }, [showSlides, slideIndex]);

  const currentSlide = useCallback((n) => {
    showSlides(n);
  }, [showSlides]);

  const isValidEmail = useCallback((email) => {
    const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return emailRegex.test(email);
  }, []);

  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
      setAuthLoading(false);
    };

    checkLoginStatus(); // Check status on mount
    window.addEventListener('storage', checkLoginStatus); // Listen for storage changes

    // Review Carousel
    let reviewAutoScrollInterval;
    const updateReviewPosition = () => {
      if (reviewTrackRef.current && reviewItemsRef.current.length > 0) {
        const reviewWidth = reviewItemsRef.current[0].offsetWidth;
        reviewTrackRef.current.style.transform = `translateX(${-reviewIndex * reviewWidth}px)`;
      }
    };

    const nextReview = () => {
      setReviewIndex(prevIndex => (prevIndex + 1) % reviewItemsRef.current.length);
    };

    const prevReview = () => {
      setReviewIndex(prevIndex => (prevIndex - 1 + reviewItemsRef.current.length) % reviewItemsRef.current.length);
    };

    const startReviewAutoScroll = () => {
      reviewAutoScrollInterval = setInterval(() => {
        nextReview();
      }, 3000);
    };

    const stopReviewAutoScroll = () => {
      clearInterval(reviewAutoScrollInterval);
    };

    startReviewAutoScroll();

    // Event Listeners for review buttons
    const prevButton = document.getElementById('prev-button');
    const nextButton = document.getElementById('next-button');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        stopReviewAutoScroll();
        prevReview();
      });
    }
    if (nextButton) {
      nextButton.addEventListener('click', () => {
        stopReviewAutoScroll();
        nextReview();
      });
    }

    // Form Submission
    const mailListForm = document.getElementById('mail-list-form');
    if (mailListForm) {
      mailListForm.addEventListener('submit', function (event) {
        event.preventDefault();

        const email = emailInputRef.current.value;

        if (!isValidEmail(email)) {
          // Client-side validation already handles this, no need for an overlay
          return;
        }

        if (emailInputRef.current) emailInputRef.current.value = '';

        // Send email to backend API
        fetch('https://best3intennis.com/best3intennis-api/api/subscribe.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: email }),
        })
          .then(response => response.json())
          .then(data => {
            if (data.status === 'success') {
              setShowConfirmationOverlay(true);
              setTimeout(() => setShowConfirmationOverlay(false), 3000);
            } else {
              setOverlayMessage(data.message);
              setShowErrorOverlay(true);
              setTimeout(() => setShowErrorOverlay(false), 3000);
            }
          })
          .catch(error => {
            console.error('Error:', error);
            setOverlayMessage('An error occurred. Please try again.');
            setShowErrorOverlay(true);
            setTimeout(() => setShowErrorOverlay(false), 3000);
          });
      });
    }

    return () => {
      clearInterval(reviewAutoScrollInterval);
      window.removeEventListener('storage', checkLoginStatus); // Clean up event listener
    };
  }, [reviewIndex, isValidEmail]);

  // Effect for automatic image carousel
  useEffect(() => {
    let autoSlideInterval;
    if (slidesRef.current.length > 0 && dotsRef.current.length > 0) {
      showSlides(slideIndex); // Initialize the first slide
      autoSlideInterval = setInterval(() => {
        setSlideIndex(prevIndex => {
          let newIndex = prevIndex + 1;
          if (newIndex > slidesRef.current.length) {
            newIndex = 1;
          }
          showSlides(newIndex);
          return newIndex;
        });
      }, 5000);
    }

    return () => {
      clearInterval(autoSlideInterval);
    };
  }, [slideIndex, showSlides, slidesRef.current.length, dotsRef.current.length]);

  useEffect(() => {
    // This useEffect is now redundant as showSlides is called in the auto-carousel effect
    // showSlides(slideIndex);
  }, [slideIndex, showSlides]);

  useEffect(() => {
    if (reviewTrackRef.current && reviewItemsRef.current.length > 0) {
      const reviewWidth = reviewItemsRef.current[0].offsetWidth;
      reviewTrackRef.current.style.transform = `translateX(${-reviewIndex * reviewWidth}px)`;
    }
  }, [reviewIndex]);

  if (authLoading) {
    return <div>Loading authentication status...</div>;
  }

  return (
    <div>
      <main className="container mx-auto px-4 py-8 flex-grow flex flex-col items-center justify-center mb-16"> {/* Added mb-16 for spacing */}
        <div className="text-center space-y-6 max-w-3xl">
          <h2 className="text-4xl font-bold text-green-800">Track Your Tennis Journey</h2>
          <p className="text-lg text-gray-700">The ultimate companion for tennis players of all levels. Record your matches, track your progress, and improve your game.</p>
          <div className="carousel-container" id="image-carousel">
            <div className="slide fade" ref={el => slidesRef.current[0] = el} style={{ display: 'none' }}>
              <img src="Images/LogBook_cover2.png" alt="Logbook Cover Practice" />
            </div>
            <div className="slide fade" ref={el => slidesRef.current[1] = el} style={{ display: 'none' }}>
              <img src="Images/LogBook_Page1.png" alt="Logbook Page 1" />
            </div>
            <div className="slide fade" ref={el => slidesRef.current[2] = el} style={{ display: 'none' }}>
              <img src="Images/LogBook_Page2.png" alt="Logbook Page 2" />
            </div>
            <div className="slide fade" ref={el => slidesRef.current[3] = el} style={{ display: 'none' }}>
              <img src="Images/LogBook_cover1.png" alt="Logbook Cover Match" />
            </div>
            <div className="slide fade" ref={el => slidesRef.current[4] = el} style={{ display: 'block' }}>
              <img src="Images/LogBook_Page3.png" alt="Logbook Page 3" />
            </div>
            <div className="slide fade" ref={el => slidesRef.current[5] = el} style={{ display: 'none' }}>
              <img src="Images/LogBook_Page4.png" alt="Logbook Page 4" />
            </div>
            <span className="prev" onClick={() => plusSlides(-1)}>❮</span>
            <span className="next" onClick={() => plusSlides(1)}>❯</span>
            <div className="dots" id="carousel-nav">
              <span className="dot" ref={el => dotsRef.current[0] = el} onClick={() => currentSlide(1)}></span>
              <span className="dot" ref={el => dotsRef.current[1] = el} onClick={() => currentSlide(2)}></span>
              <span className="dot" ref={el => dotsRef.current[2] = el} onClick={() => currentSlide(3)}></span>
              <span className="dot" ref={el => dotsRef.current[3] = el} onClick={() => currentSlide(4)}></span>
              <span className="dot active" ref={el => dotsRef.current[4] = el} onClick={() => currentSlide(5)}></span>
              <span className="dot" ref={el => dotsRef.current[5] = el} onClick={() => currentSlide(6)}></span>
            </div>
          </div>
          <div className="flex justify-center space-x-4 mt-6">
            <a href="#buy" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300">Buy Now</a>
          </div>
        </div>
      </main>

      <section id="features" className="bg-green-50 py-12">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-semibold text-green-800 text-center mb-8">Key Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 flex items-start">
              <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/book-open.svg" alt="Match Records" className="w-8 h-8 text-green-500 mr-4" />
              <div>
                <h4 className="text-xl font-semibold text-green-800 mb-2">Match Records</h4>
                <p className="text-gray-700">Detailed sections to record match scores, opponents, and strategies.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 flex items-start">
              <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/calendar.svg" alt="Practice Logs" className="w-8 h-8 text-green-500 mr-4" />
              <div>
                <h4 className="text-xl font-semibold text-green-800 mb-2">Practice Logs</h4>
                <p className="text-gray-700">Dedicated space to track your practice sessions and drills.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 flex items-start">
              <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/trending-up.svg" alt="Progress Tracking" className="w-8 h-8 text-green-500 mr-4" />
              <div>
                <h4 className="text-xl font-semibold text-green-800 mb-2">Progress Tracking</h4>
                <p className="text-gray-700">Monitor your improvement over time with dedicated tracking pages.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 flex items-start">
              <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/clipboard-pen.svg" alt="Notes Section" className="w-8 h-8 text-green-500 mr-4" />
              <div>
                <h4 className="text-xl font-semibold text-green-800 mb-2">Notes Section</h4>
                <p className="text-gray-700">Plenty of space to jot down notes, reflections, and goals.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 flex items-start">
              <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/target.svg" alt="Goal Setting" className="w-8 h-8 text-green-500 mr-4" />
              <div>
                <h4 className="text-xl font-semibold text-green-800 mb-2">Goal Setting</h4>
                <p className="text-gray-700">Set your tennis goals and track your progress towards achieving them.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 flex items-start">
              <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/heart.svg" alt="Motivation" className="w-8 h-8 text-green-500 mr-4" />
              <div>
                <h4 className="text-xl font-semibold text-green-800 mb-2">Motivation</h4>
                <p className="text-gray-700">Stay motivated with inspirational quotes and tips.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="benefits" className="bg-yellow-50 py-12">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-semibold text-green-800 text-center mb-8">Benefits of Using a Tennis Logbook</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Improved Tracking" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Improved tracking of your tennis progress and performance.</p>
              </div>
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Enhanced Self-Awareness" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Enhanced self-awareness of your strengths and weaknesses.</p>
              </div>
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Better Goal Setting" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Better goal setting and achievement.</p>
              </div>
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Increased Motivation" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Increased motivation and accountability.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Valuable Insights" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Valuable insights into your game for continuous improvement.</p>
              </div>
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Organized Records" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Organized record of your tennis journey.</p>
              </div>
              <div className="flex items-start">
                <img src="https://cdn.jsdelivr.net/npm/lucide-static@latest/icons/check-circle-2.svg" alt="Easy to Use" className="w-6 h-6 text-green-600 mr-2" />
                <p className="text-lg text-gray-700">Easy to use and track progress.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="reviews" className="bg-green-50 py-12">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-semibold text-green-800 text-center mb-8">What Players Are Saying</h3>
          <div id="review-carousel" className="relative overflow-hidden rounded-lg">
            <div className="absolute inset-0 bg-gradient-to-r from-green-50 to-yellow-50 opacity-50"></div>
            <div ref={reviewTrackRef} className="relative whitespace-nowrap transition-transform duration-500 ease-in-out" id="review-track" style={{ transform: `translateX(${-reviewIndex * (reviewItemsRef.current[0]?.offsetWidth || 0)}px)` }}>
              <div ref={el => reviewItemsRef.current[0] = el} className="inline-block w-full md:w-1/2 lg:w-1/3 px-4 py-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-gray-700 italic mb-4 whitespace-normal">"This logbook has been a game-changer for me. I love tracking my progress and seeing how far I've come."</p>
                  <p className="text-green-800 font-semibold">- Sarah J.</p>
                </div>
              </div>
              <div ref={el => reviewItemsRef.current[1] = el} className="inline-block w-full md:w-1/2 lg:w-1/3 px-4 py-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-gray-700 italic mb-4 whitespace-normal">"A must-have for any serious tennis player. It helps me stay organized and focused on my goals."</p>
                  <p className="text-green-800 font-semibold">- Michael K.</p>
                </div>
              </div>
              <div ref={el => reviewItemsRef.current[2] = el} className="inline-block w-full md:w-1/2 lg:w-1/3 px-4 py-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-gray-700 italic mb-4 whitespace-normal">"I highly recommend this logbook to anyone looking to improve their tennis game. It's a great tool for self-reflection."</p>
                  <p className="text-green-800 font-semibold">- Emily R.</p>
                </div>
              </div>
              <div ref={el => reviewItemsRef.current[3] = el} className="inline-block w-full md:w-1/2 lg:w-1/3 px-4 py-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                  <p className="text-gray-700 italic mb-4 whitespace-normal">"I love the layout and how easy it is to track matches and practice."</p>
                  <p className="text-green-800 font-semibold">- David L.</p>
                </div>
              </div>
            </div>
            <button id="prev-button" className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-green-500 text-white rounded-full p-2 cursor-pointer hover:bg-green-600 transition duration-300">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"></path>
              </svg>
            </button>
            <button id="next-button" className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-green-500 text-white rounded-full p-2 cursor-pointer hover:bg-green-600 transition duration-300">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"></path>
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section id="mail-list" className="bg-yellow-100 py-12">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-semibold text-green-800 mb-4">Subscribe Today</h3>
          <p className="text-lg text-gray-700 mb-6">
            Subscribe to our mailing list and receive latest updates and discounts.
          </p>
          <form id="mail-list-form" className="flex flex-col sm:flex-row justify-center gap-4">
            <input type="email" id="email-input" ref={emailInputRef} placeholder="Enter your email address" required="" className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 focus:ring focus:ring-green-200 focus:outline-none text-gray-700" />
            <button type="submit" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300">
              Subscribe Now
            </button>
          </form>
        </div>
      </section>

      <section id="buy" className="bg-yellow-100 py-12">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-semibold text-green-800 mb-4">Ready to Elevate Your Game?</h3>
          <p className="text-lg text-gray-700 mb-6">
            Choose the logbook that best suits your needs:
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-6">
            <div className="bg-white rounded-lg shadow-md p-6 max-w-sm">
              <h4 className="text-xl font-semibold text-green-800 mb-2">Competition Logbook</h4>
              <img src="https://m.media-amazon.com/images/I/61EhNoC1JAL._SX445_.jpg" alt="Practice Book" />
              <p className="text-gray-700 mb-4">
                Track your match performance, analyze your results, and plan your strategy for competitive play.
              </p>
              <a href="https://www.amazon.ca/dp/B0D7TT3XP4" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300">
                Buy on Amazon
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 max-w-sm">
              <h4 className="text-xl font-semibold text-green-800 mb-2">Practice Logbook</h4>
              <img src="https://m.media-amazon.com/images/I/51xHR3QtTjL._SX445_.jpg" alt="Practice Book" />
              <p className="text-gray-700 mb-4">
                Record your practice sessions, track your skill development, and monitor your progress towards your goals.
              </p>
              <a href="https://www.amazon.ca/dp/B0D7QPRZZ7" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300">
                Buy on Amazon
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-gray-100 text-gray-600 py-4 text-center">
        <p>© 2024 Tennis Logbooks. All rights reserved.</p>
      </footer>

      {showConfirmationOverlay && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <h3 className="text-2xl font-bold text-green-600 mb-4">Thank You for Subscribing!</h3>
            <p className="text-gray-700 mb-6">You've been added to our mailing list.</p>
            <button
              onClick={() => setShowConfirmationOverlay(false)}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showErrorOverlay && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <h3 className="text-2xl font-bold text-red-600 mb-4">Subscription Error</h3>
            <p className="text-gray-700 mb-6">{overlayMessage}</p>
            <button
              onClick={() => setShowErrorOverlay(false)}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-md transition duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogBookHubPage;

